import { useState, useEffect } from 'react';
import api from '../services/api';
import SearchInput from '../components/SearchInput';
import { usePagination } from '../hooks/usePagination';
import { useDebounce } from '../hooks/useDebounce';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { 
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../components/ui/pagination';
import { Plus, Trash2, Edit2, User as UserIcon } from 'lucide-react';
import { toast } from 'sonner';

const NONE = '__none__';

const emptyUserForm = {
  email: '',
  name: '',
  password: '',
  role: 'USER',
  defaultStockId: NONE,
};

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [formData, setFormData] = useState(emptyUserForm);

  // Pagination state
  const [paginationData, setPaginationData] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });

  const pagination = usePagination({
    initialPage: 1,
    itemsPerPage: 10,
    totalItems: paginationData.totalItems,
  });

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, debouncedSearchTerm]);

  useEffect(() => {
    const loadStocks = async () => {
      try {
        const response = await api.getStocks({ page: 1, limit: 100 });
        setStocks(response.data || []);
      } catch (error) {
        toast.error('Erro ao carregar estoques');
        setStocks([]);
      }
    };

    loadStocks();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await api.getUsers({
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
        search: debouncedSearchTerm,
      });
      
      if (response.data && response.pagination) {
        setUsers(response.data);
        setPaginationData(response.pagination);
      } else {
        setUsers(Array.isArray(response) ? response : []);
        setPaginationData({
          currentPage: 1,
          totalPages: 1,
          totalItems: Array.isArray(response) ? response.length : 0,
          itemsPerPage: 10,
        });
      }
    } catch (error) {
      toast.error('Erro ao carregar usuários');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        name: user.name,
        password: '',
        role: user.role,
        defaultStockId: user.defaultStockId || NONE,
      });
    } else {
      setEditingUser(null);
      setFormData(emptyUserForm);
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const defaultStockId = formData.defaultStockId === NONE ? null : formData.defaultStockId;

    try {
      if (editingUser) {
        // Update user (without password)
        await api.updateUser(editingUser.id, {
          name: formData.name,
          role: formData.role,
          defaultStockId,
        });
        toast.success('Usuário atualizado com sucesso');
      } else {
        // Create new user
        await api.createUser({ ...formData, defaultStockId });
        toast.success('Usuário criado com sucesso');
      }

      setDialogOpen(false);
      setEditingUser(null);
      setFormData(emptyUserForm);
      loadUsers();
    } catch (error) {
      toast.error(error.message || 'Erro ao salvar usuário');
    }
  };

  const handleToggleEnabled = async (user) => {
    try {
      await api.toggleUser(user.id);
      toast.success(`Usuário ${!user.enabled ? 'ativado' : 'desativado'} com sucesso`);
      loadUsers();
    } catch (error) {
      toast.error('Erro ao alterar status do usuário');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja deletar este usuário?')) return;
    try {
      await api.deleteUser(id);
      toast.success('Usuário deletado');
      loadUsers();
    } catch (error) {
      toast.error('Erro ao deletar usuário');
    }
  };

  const handlePageChange = (page) => {
    pagination.goToPage(page);
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Usuários</h1>
          <p className="text-muted-foreground mt-1">Gerencie os usuários do sistema</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => handleOpenDialog()} data-testid="add-user-button">
              <Plus className="w-4 h-4 mr-2" />
              Novo Usuário
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[520px]">
            <DialogHeader>
              <DialogTitle>{editingUser ? 'Editar Usuário' : 'Criar Novo Usuário'}</DialogTitle>
              <DialogDescription>
                {editingUser ? 'Atualize os dados do usuário' : 'Adicione um novo usuário ao sistema'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              {!editingUser && (
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
              )}
              {!editingUser && (
                <div className="space-y-2">
                  <Label htmlFor="password">Senha</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    minLength={6}
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="role">Função</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USER">Usuário</SelectItem>
                    <SelectItem value="ADMIN">Administrador</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="defaultStockId">Estoque padrão</Label>
                <Select
                  value={formData.defaultStockId}
                  onValueChange={(value) => setFormData({ ...formData, defaultStockId: value })}
                >
                  <SelectTrigger id="defaultStockId">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Sem estoque padrão</SelectItem>
                    {stocks.map((stock) => (
                      <SelectItem key={stock.id} value={stock.id}>
                        {stock.code} - {stock.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full">
                {editingUser ? 'Atualizar Usuário' : 'Criar Usuário'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search Input */}
      <SearchInput
        value={searchTerm}
        onChange={handleSearchChange}
        placeholder="Pesquisar por nome ou email..."
        searchFields={['Nome', 'Email']}
      />

      {/* Users Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-6 py-3 text-left text-sm font-semibold">Nome</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Email</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Estoque padrão</th>
                <th className="px-6 py-3 text-center text-sm font-semibold">Função</th>
                <th className="px-6 py-3 text-center text-sm font-semibold">Status</th>
                <th className="px-6 py-3 text-right text-sm font-semibold">Ações</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr
                  key={user.id}
                  className="border-b hover:bg-muted/50 transition-colors"
                  data-testid={`user-row-${user.id}`}
                >
                  <td className="px-6 py-4 text-sm font-medium">{user.name}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{user.email}</td>
                  <td className="px-6 py-4 text-sm">
                    {user.defaultStock ? `${user.defaultStock.code} - ${user.defaultStock.name}` : '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-center">
                    <Badge variant={user.role === 'ADMIN' ? 'default' : 'secondary'}>
                      {user.role === 'ADMIN' ? 'Administrador' : 'Usuário'}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 text-sm text-center">
                    <Switch
                      checked={user.enabled}
                      onCheckedChange={() => handleToggleEnabled(user)}
                      aria-label={`${user.enabled ? 'Desativar' : 'Ativar'} usuário`}
                    />
                  </td>
                  <td className="px-6 py-4 text-sm text-right space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenDialog(user)}
                      data-testid={`edit-user-${user.id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(user.id)}
                      data-testid={`delete-user-${user.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Pagination */}
      {paginationData.totalPages > 1 && (
        <Pagination className="mt-4">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious 
                onClick={() => handlePageChange(paginationData.currentPage - 1)}
                className={paginationData.currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
              />
            </PaginationItem>
            
            {Array.from({ length: paginationData.totalPages }, (_, i) => i + 1).map((page) => (
              <PaginationItem key={page}>
                <PaginationLink
                  onClick={() => handlePageChange(page)}
                  isActive={page === paginationData.currentPage}
                  className="cursor-pointer"
                >
                  {page}
                </PaginationLink>
              </PaginationItem>
            ))}
            
            <PaginationItem>
              <PaginationNext 
                onClick={() => handlePageChange(paginationData.currentPage + 1)}
                className={paginationData.currentPage === paginationData.totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}

      {users.length === 0 && !loading && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <UserIcon className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-center">
              {searchTerm ? 'Nenhum usuário encontrado.' : 'Nenhum usuário encontrado.'}
              {!searchTerm && ' Crie o primeiro!'}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default UsersPage;
